import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ReportsPage.css';

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    userId: '',
    name: '',
    minAge: '',
    maxAge: '',
    gender: '',
    state: '',
    religion: '',
    occupation: '',
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await axios.get('https://back.varankal.com/app/userreports/admin/matrimony-reports');
        const data = res.data.data || [];
        setReports(data);
        setFilteredReports(data);
      } catch (err) {
        console.error('Error fetching matrimony reports:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    let tempReports = [...reports];

    if (filters.userId.trim()) {
      const userIdFilter = filters.userId.toLowerCase();
      tempReports = tempReports.filter(r =>
        (r.userId || '').toLowerCase().includes(userIdFilter)
      );
    }

    if (filters.name.trim()) {
      const nameFilter = filters.name.toLowerCase();
      tempReports = tempReports.filter(r =>
        r.basicDetails?.name?.toLowerCase().includes(nameFilter)
      );
    }

    if (filters.gender) {
      tempReports = tempReports.filter(r => r.basicDetails?.gender === filters.gender);
    }

    if (filters.state) {
      tempReports = tempReports.filter(r => r.profileDetails?.location?.state === filters.state);
    }

    if (filters.religion) {
      tempReports = tempReports.filter(r => r.profileDetails?.religion === filters.religion);
    }

    if (filters.occupation) {
      tempReports = tempReports.filter(r => r.profileDetails?.occupation === filters.occupation);
    }

    if (filters.minAge) {
      const minAge = parseInt(filters.minAge, 10);
      if (!isNaN(minAge)) {
        tempReports = tempReports.filter(r => r.basicDetails?.age >= minAge);
      }
    }

    if (filters.maxAge) {
      const maxAge = parseInt(filters.maxAge, 10);
      if (!isNaN(maxAge)) {
        tempReports = tempReports.filter(r => r.basicDetails?.age <= maxAge);
      }
    }

    // Date filtering based on createdAt
    if (startDate || endDate) {
      tempReports = tempReports.filter(r => {
        const createdDate = new Date(r.createdAt);
        if (startDate && createdDate < startDate) return false;
        if (endDate && createdDate > endDate) return false;
        return true;
      });
    }

    setFilteredReports(tempReports);
  }, [filters, reports, startDate, endDate]);

  const exportToExcel = () => {
    const worksheetData = filteredReports.map(report => ({
      UserID: report.userId || '',
      Name: report?.basicDetails?.name || '',
      Email: report?.basicDetails?.email || '',
      Phone: report?.basicDetails?.phoneNumber || '',
      Age: report?.basicDetails?.age || '',
      Gender: report?.basicDetails?.gender || '',
      DOB: report?.basicDetails?.dateOfBirth
        ? new Date(report.basicDetails.dateOfBirth).toLocaleDateString()
        : '',
      Qualification: report?.basicDetails?.qualification || '',
      FatherName: report?.profileDetails?.fatherName || '',
      FatherOccupation: report?.profileDetails?.fatherOccupation || '',
      MotherName: report?.profileDetails?.motherName || '',
      MotherOccupation: report?.profileDetails?.motherOccupation || '',
      NoOfSiblings: report?.profileDetails?.noOfSiblings ?? '',
      NoOfBrothers: report?.profileDetails?.noOfBrothers ?? '',
      NoOfSisters: report?.profileDetails?.noOfSisters ?? '',
      NoOfMarried: report?.profileDetails?.noOfMarried ?? '',
      NoOfUnmarried: report?.profileDetails?.noOfUnmarried ?? '',
      FamilyClass: report?.profileDetails?.familyClass || '',
      FamilyOrientation: report?.profileDetails?.familyOrientation || '',
      UserIncome: report?.profileDetails?.userIncome || '',
      Occupation: report?.profileDetails?.occupation || '',
      Height: report?.profileDetails?.height ?? '',
      Weight: report?.profileDetails?.weight ?? '',
      Complexion: report?.profileDetails?.complexion || '',
      Disability: report?.profileDetails?.disability || '',
      Religion: report?.profileDetails?.religion || '',
      Caste: report?.profileDetails?.caste || '',
      SubCaste: report?.profileDetails?.subCaste || '',
      State: report?.profileDetails?.location?.state || '',
      District: report?.profileDetails?.location?.district || '',
      Hobbies: report?.profileDetails?.hobbies || '',
      Interest: report?.profileDetails?.interest || '',
      DrinkingHabits: report?.profileDetails?.drinkingHabits || '',
      SmokingHabits: report?.profileDetails?.smokingHabits || '',
      Horoscope: report?.profileDetails?.horoscope || '',
      CreatedAt: report?.createdAt ? new Date(report.createdAt).toLocaleDateString() : '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Matrimony Reports');
    XLSX.writeFile(workbook, 'Matrimony_Reports.xlsx');
  };

  const exportToPDF = () => {
    const input = document.getElementById('matrimony-tableGG1');
    if (!input) {
      console.error('Export element not found.');
      return;
    }
    html2canvas(input).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('Matrimony_Reports.pdf');
    }).catch(err => {
      console.error('Error generating PDF:', err);
    });
  };

  if (loading) return <p className="loading-textGG1">Loading...</p>;

  const uniqueGenders = [...new Set(reports.map(r => r.basicDetails?.gender).filter(Boolean))];
  const uniqueStates = [...new Set(reports.map(r => r.profileDetails?.location?.state).filter(Boolean))];
  const uniqueReligions = [...new Set(reports.map(r => r.profileDetails?.religion).filter(Boolean))];
  const uniqueOccupations = [...new Set(reports.map(r => r.profileDetails?.occupation).filter(Boolean))];

  return (
    <div className="matrimony-report-containerGG1">
      <h2 className="titleGG1">Varankal Matrimony Reports</h2>

      <div className="filters-containerGG1">
        <input type="text" name="userId" placeholder="Filter by User ID" value={filters.userId} onChange={handleFilterChange} className="filter-inputGG11" />
        <input type="text" name="name" placeholder="Filter by Name" value={filters.name} onChange={handleFilterChange} className="filter-inputGG111" />
        <input type="number" name="minAge" placeholder="Min Age" value={filters.minAge} onChange={handleFilterChange} min={0} className="filter-inputGG121" />
        <input type="number" name="maxAge" placeholder="Max Age" value={filters.maxAge} onChange={handleFilterChange} min={0} className="filter-inputGG112" />

        <select name="gender" value={filters.gender} onChange={handleFilterChange} className="filter-selectGG1">
          <option value="">All Genders</option>
          {uniqueGenders.map(g => <option key={g} value={g}>{g}</option>)}
        </select>

        <select name="state" value={filters.state} onChange={handleFilterChange} className="filter-selectGG1">
          <option value="">All States</option>
          {uniqueStates.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select name="religion" value={filters.religion} onChange={handleFilterChange} className="filter-selectGG1">
          <option value="">All Religions</option>
          {uniqueReligions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <select name="occupation" value={filters.occupation} onChange={handleFilterChange} className="filter-selectGG1">
          <option value="">All Occupations</option>
          {uniqueOccupations.map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        {/* Date Range Filter */}
        <div className="date-filterGG1">
          <label>From:</label>
          <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} className="date-pickerGG1" placeholderText="Start Date" />
        </div>

        <div className="date-filterGG1">
          <label>To:</label>
          <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} className="date-pickerGG1" placeholderText="End Date" />
        </div>
      </div>

      <div className="button-groupGG1">
        <button onClick={exportToExcel} className="export-buttonGG1">Download Excel</button>
        <button onClick={exportToPDF} className="export-buttonGG1">Download PDF</button>
      </div>

      <div id="matrimony-tableGG1" className="table-containerGG1">
        <table className="matrimony-tableGG1">
          <thead>
            <tr>
              <th>#</th>
              <th>User ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Age</th>
              <th>Gender</th>
              <th>DOB</th>
              <th>Qualification</th>
              <th>Father Name</th>
              <th>Father Occupation</th>
              <th>Mother Name</th>
              <th>Mother Occupation</th>
              <th>No of Siblings</th>
              <th>No of Brothers</th>
              <th>No of Sisters</th>
              <th>No of Married</th>
              <th>No of Unmarried</th>
              <th>Family Class</th>
              <th>Family Orientation</th>
              <th>User Income</th>
              <th>Occupation</th>
              <th>Height</th>
              <th>Weight</th>
              <th>Complexion</th>
              <th>Disability</th>
              <th>Religion</th>
              <th>Caste</th>
              <th>Sub Caste</th>
              <th>State</th>
              <th>District</th>
              <th>Hobbies</th>
              <th>Interest</th>
              <th>Drinking Habits</th>
              <th>Smoking Habits</th>
              <th>Horoscope</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.length === 0 ? (
              <tr><td colSpan="15" style={{ textAlign: 'center' }}>No reports found.</td></tr>
            ) : (
              filteredReports.map((report, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{report.userId || ''}</td>
                  <td>{report.basicDetails?.name || ''}</td>
                  <td>{report.basicDetails?.email || ''}</td>
                  <td>{report.basicDetails?.phoneNumber || ''}</td>
                  <td>{report.basicDetails?.age || ''}</td>
                  <td>{report.basicDetails?.gender || ''}</td>
                  <td>{report.basicDetails?.dateOfBirth ? new Date(report.basicDetails.dateOfBirth).toLocaleDateString() : ''}</td>
                  <td>{report.basicDetails?.qualification || ''}</td>
                  <td>{report.profileDetails?.fatherName || ''}</td>
                  <td>{report.profileDetails?.fatherOccupation || ''}</td>
                  <td>{report.profileDetails?.motherName || ''}</td>
                  <td>{report.profileDetails?.motherOccupation || ''}</td>
                  <td>{report.profileDetails?.noOfSiblings ?? ''}</td>
                  <td>{report.profileDetails?.noOfBrothers ?? ''}</td>
                  <td>{report.profileDetails?.noOfSisters ?? ''}</td>
                  <td>{report.profileDetails?.noOfMarried ?? ''}</td>
                  <td>{report.profileDetails?.noOfUnmarried ?? ''}</td>
                  <td>{report.profileDetails?.familyClass || ''}</td>
                  <td>{report.profileDetails?.familyOrientation || ''}</td>
                  <td>{report.profileDetails?.userIncome || ''}</td>
                  <td>{report.profileDetails?.occupation || ''}</td>
                  <td>{report.profileDetails?.height ?? ''}</td>
                  <td>{report.profileDetails?.weight ?? ''}</td>
                  <td>{report.profileDetails?.complexion || ''}</td>
                  <td>{report.profileDetails?.disability || ''}</td>
                  <td>{report.profileDetails?.religion || ''}</td>
                  <td>{report.profileDetails?.caste || ''}</td>
                  <td>{report.profileDetails?.subCaste || ''}</td>
                  <td>{report.profileDetails?.location?.state || ''}</td>
                  <td>{report.profileDetails?.location?.district || ''}</td>
                  <td>{report.profileDetails?.hobbies || ''}</td>
                  <td>{report.profileDetails?.interest || ''}</td>
                  <td>{report.profileDetails?.drinkingHabits || ''}</td>
                  <td>{report.profileDetails?.smokingHabits || ''}</td>
                  <td>{report.profileDetails?.horoscope || ''}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportsPage;

